import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ko, { type TranslationKey } from './ko'
import en from './en'

export type Lang = 'ko' | 'en'

export type TFunction = (
  key: TranslationKey,
  vars?: Record<string, string | number>
) => string

const dicts: Record<Lang, Record<TranslationKey, string>> = { ko, en }

const STORAGE_KEY = 'appLang'

// The product uses two URL schemes:
//   • Locale-prefixed routes:   `/teacher`, `/en/teacher`, `/`, `/en`, …
//     (these encode the lang in the path itself)
//   • Language-agnostic routes: `/admin`, `/sign-in`, `/sign-up`, …
//     (admin + Clerk widgets — no locale segment)
//
// On locale-prefixed routes, lang is derived directly from the URL.
// On language-agnostic routes, we fall back to the user's stored
// preference (or default to Korean, the product's primary audience).
//
// ``/privacy`` is locale-agnostic per PIPA Art. 30 (#127) — the
// policy uses its own ``?lang=en`` query param so an admin reading
// the policy can flip languages without changing the rest of the
// app's locale.
const LOCALE_AGNOSTIC_PREFIXES = [
  '/admin',
  '/sign-in',
  '/sign-up',
  '/dev',
  '/privacy',
]

function isLocaleAgnosticPath(pathname: string): boolean {
  return LOCALE_AGNOSTIC_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + '/'),
  )
}

function readStoredLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'en' ? 'en' : 'ko'
}

function resolveLang(pathname: string): Lang {
  if (isLocaleAgnosticPath(pathname)) return readStoredLang()
  // Locale-prefixed tree: `/en` or `/en/...` → English, everything else → Korean.
  return /^\/en(\/|$)/.test(pathname) ? 'en' : 'ko'
}

type LangContextValue = {
  lang: Lang
  t: TFunction
  toggle: () => void
}

const LangContext = createContext<LangContextValue | null>(null)

/**
 * Single source of truth for the app's active language.
 *
 * Must sit inside a ``<BrowserRouter>`` so the provider can react to
 * URL changes via ``useLocation``. Everything else — the theme provider,
 * Clerk provider, page components — can live underneath.
 */
export function LangProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [lang, setLang] = useState<Lang>(() =>
    resolveLang(window.location.pathname),
  )

  // URL-driven updates: when the pathname changes, re-derive lang.
  // This fires both when the user navigates between locale-prefixed
  // routes (/teacher ↔ /en/teacher) and when they hop onto a
  // language-agnostic route (lang sticks to the stored preference).
  // setState-in-effect here is the legitimate "subscribe to external
  // state" use the rule's docstring lists — the URL is external to
  // React. Lang isn't in deps because including it would create a
  // self-feedback loop (setLang → lang change → effect re-runs).
  useEffect(() => {
    const next = resolveLang(location.pathname)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (next !== lang) setLang(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Persist whenever lang changes so language-agnostic routes have a
  // "remember my choice" experience between visits.
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang)
    document.documentElement.lang = lang
  }, [lang])

  const t = useCallback<TFunction>(
    (key, vars) => {
      const msg = dicts[lang][key] ?? key
      if (!vars) return msg
      return Object.entries(vars).reduce(
        (s, [k, v]) => s.replaceAll(`{${k}}`, String(v)),
        msg,
      )
    },
    [lang],
  )

  // Toggle behaviour depends on where the user is:
  //   • On a locale-prefixed route → flip the `/en` prefix on/off, then
  //     let the URL-driven effect re-derive lang.
  //   • On a language-agnostic route (admin, Clerk pages) → flip state
  //     directly; the persist effect writes the new preference.
  const toggle = useCallback(() => {
    const next: Lang = lang === 'en' ? 'ko' : 'en'
    if (isLocaleAgnosticPath(location.pathname)) {
      setLang(next)
      return
    }
    const stripped = location.pathname.replace(/^\/en(?=\/|$)/, '') || '/'
    const newPath =
      next === 'en'
        ? stripped === '/'
          ? '/en'
          : `/en${stripped}`
        : stripped
    navigate(newPath + location.search + location.hash)
  }, [lang, location, navigate])

  const value = useMemo(() => ({ lang, t, toggle }), [lang, t, toggle])

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>
}

function useLangCtx(): LangContextValue {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error('useT/useLang must be used inside <LangProvider>')
  return ctx
}

/** Translate a key. Supports `{name}` substitution via the second arg. */
export function useT(): TFunction {
  return useLangCtx().t
}

/** Current language code. */
export function useLang(): Lang {
  return useLangCtx().lang
}

/**
 * Turn an internal path like "/student/session/42" into the URL for the
 * current language ("/en/student/session/42" when lang === "en").
 * Pass paths *without* the /en prefix; this hook adds it when needed.
 */
export function useLocalizedPath(): (path: string) => string {
  const { lang } = useLangCtx()
  return useCallback(
    (path: string) => {
      const normalized = path.startsWith('/') ? path : `/${path}`
      return lang === 'en' ? `/en${normalized}` : normalized
    },
    [lang],
  )
}

/**
 * Toggle helper: flips between Korean and English. On locale-prefixed
 * routes it updates the URL; on admin / Clerk pages it updates the
 * stored preference in-place.
 */
export function useLanguageToggle(): () => void {
  return useLangCtx().toggle
}
